// Stylised India map with Pune ↔ Aurangabad corridor.
// Uses a custom SVG outline + interactive city markers.
import { useState } from "react";

const CITIES = [
    { name: "Aurangabad", code: "AUR", x: 360, y: 360, primary: true },
    { name: "Pune", code: "PNQ", x: 320, y: 410, primary: true },
    { name: "Mumbai", code: "BOM", x: 285, y: 395 },
    { name: "Nashik", code: "NSK", x: 315, y: 360 },
    { name: "Nagpur", code: "NAG", x: 445, y: 365 },
    { name: "Ahmedabad", code: "AMD", x: 270, y: 305 },
    { name: "Delhi", code: "DEL", x: 360, y: 195 },
    { name: "Bengaluru", code: "BLR", x: 385, y: 525 },
    { name: "Chennai", code: "MAA", x: 445, y: 540 },
    { name: "Hyderabad", code: "HYD", x: 410, y: 460 },
    { name: "Kolkata", code: "CCU", x: 560, y: 345 },
    { name: "Indore", code: "IDR", x: 340, y: 320 },
    { name: "Surat", code: "STV", x: 285, y: 340 },
    { name: "Jaipur", code: "JAI", x: 325, y: 245 },
];

// Highly stylised India outline (not geographically exact, designed for visual impact)
const INDIA_PATH =
    "M340 100 L390 110 L430 135 L470 160 L490 195 L500 235 L540 250 L585 270 L605 305 L600 340 L575 365 L555 395 L545 425 L520 460 L495 490 L470 525 L445 555 L420 575 L395 555 L380 530 L355 510 L340 480 L320 445 L300 410 L285 380 L275 345 L260 315 L255 280 L260 250 L275 220 L290 190 L305 160 L325 130 Z";

export default function IndiaCorridor({ testId = "india-map" }) {
    const [hover, setHover] = useState(null);
    return (
        <div data-testid={testId} className="relative w-full">
            <svg viewBox="220 80 420 520" className="w-full h-auto" role="img" aria-label="India operations map highlighting Pune to Aurangabad corridor">
                <defs>
                    <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
                        <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#28406A" strokeWidth="0.4" opacity="0.25" />
                    </pattern>
                </defs>
                <rect x="220" y="80" width="420" height="520" fill="url(#grid)" />

                {/* India outline */}
                <path
                    d={INDIA_PATH}
                    fill="#28406A"
                    fillOpacity="0.15"
                    stroke="#4B6390"
                    strokeWidth="1.5"
                />
                {/* Inner highlight for Maharashtra region */}
                <path
                    d="M280 340 L330 335 L365 345 L380 380 L350 415 L310 420 L285 395 Z"
                    fill="#F4A000"
                    fillOpacity="0.18"
                    stroke="#F4A000"
                    strokeWidth="1.5"
                    strokeDasharray="4 3"
                />

                {/* Corridor line Pune ↔ Aurangabad */}
                <line
                    x1="320" y1="410" x2="360" y2="360"
                    stroke="#F4A000" strokeWidth="3"
                />
                <line
                    x1="320" y1="410" x2="360" y2="360"
                    stroke="#FFFFFF" strokeWidth="1.5" strokeDasharray="4 4"
                >
                    <animate attributeName="stroke-dashoffset" from="0" to="-16" dur="0.6s" repeatCount="indefinite" />
                </line>

                {/* Secondary routes from Pune outwards */}
                {[
                    [320, 410, 285, 395],
                    [360, 360, 445, 365],
                    [360, 360, 315, 360],
                    [320, 410, 410, 460],
                    [320, 410, 270, 305],
                    [360, 360, 360, 195],
                ].map(([x1, y1, x2, y2], i) => (
                    <line key={i} x1={x1} y1={y1} x2={x2} y2={y2}
                        stroke="#4B6390" strokeWidth="1" strokeDasharray="2 4" opacity="0.7" />
                ))}

                {/* City markers */}
                {CITIES.map((c) => {
                    const r = c.primary ? 9 : 4;
                    return (
                        <g
                            key={c.code}
                            onMouseEnter={() => setHover(c.code)}
                            onMouseLeave={() => setHover(null)}
                            style={{ cursor: "pointer" }}
                            data-testid={`city-${c.code.toLowerCase()}`}
                        >
                            {c.primary && (
                                <circle cx={c.x} cy={c.y} r={r + 10}
                                    fill="#F4A000" fillOpacity="0.25">
                                    <animate attributeName="r" values={`${r + 4};${r + 16};${r + 4}`} dur="2.4s" repeatCount="indefinite" />
                                    <animate attributeName="fill-opacity" values="0.4;0;0.4" dur="2.4s" repeatCount="indefinite" />
                                </circle>
                            )}
                            <circle
                                cx={c.x}
                                cy={c.y}
                                r={r}
                                fill={c.primary ? "#F4A000" : "#FFFFFF"}
                                stroke={c.primary ? "#101A2C" : "#28406A"}
                                strokeWidth="2"
                            />
                            <text
                                x={c.x + (r + 5)}
                                y={c.y + 4}
                                fontFamily="IBM Plex Sans"
                                fontSize={c.primary ? 13 : 10}
                                fontWeight={c.primary ? 700 : 500}
                                fill={c.primary ? "#101A2C" : "#4B6390"}
                            >
                                {c.name}
                            </text>
                        </g>
                    );
                })}
            </svg>

            {/* Legend cards */}
            <div className="absolute top-4 right-4 hidden md:flex flex-col gap-2 max-w-[220px]">
                <div className="bg-white border-l-4 border-[#F4A000] px-4 py-3 shadow-[2px_2px_0_#101A2C]">
                    <p className="text-[10px] tracking-[0.2em] uppercase text-[#4B6390]">Core Corridor</p>
                    <p className="font-display uppercase text-sm text-[#101A2C]">Pune ↔ Aurangabad</p>
                </div>
                <div className="bg-white border-l-4 border-[#28406A] px-4 py-3 shadow-[2px_2px_0_#101A2C]">
                    <p className="text-[10px] tracking-[0.2em] uppercase text-[#4B6390]">Pan-India</p>
                    <p className="text-xs text-[#4B6390]">via Vibhuti Transways Combines</p>
                </div>
            </div>
        </div>
    );
}
